import { Link } from "react-router-dom";
import { Target, Beaker, ShieldCheck, Microscope, ArrowUpRight } from "lucide-react";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";

const principles = [
  {
    icon: Target,
    title: "Technical over promotional",
    desc: "Every claim we make about a system is supported at the bench. We publish sensory and analytical data. Not lifestyle imagery.",
  },
  {
    icon: Microscope,
    title: "Rooted in sensory science",
    desc: "Our work is guided by trained panels, taste receptor understanding, and matrix specific evaluation. Not generic flavor theory.",
  },
  {
    icon: Beaker,
    title: "Formulator first",
    desc: "We design for the realities of manufacturing: pH drift, shear, temperature, carrier interactions, and scale up variability.",
  },
  {
    icon: ShieldCheck,
    title: "Clean, compliant, documented",
    desc: "Specifications, safety data, allergen profiles, and regulatory documentation delivered with every sample.",
  },
];

export default function About() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 border-b border-gray-100" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <span className="pm-overline">About Premier Modulations</span>
            <h1 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.04]">
              A flavor modulation partner built for the
              <span className="text-brand"> functional nutrition</span> industry.
            </h1>
          </div>
          <div className="lg:col-span-4 text-sm text-gray-600 leading-relaxed">
            We develop ingredient systems that improve the taste of
            nutritionally dense, actives heavy products, where formulation is
            hardest and where conventional flavors alone cannot solve the
            problem.
          </div>
        </div>
      </section>

      {/* Our approach */}
      <Section testId="about-approach" bg="white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="pm-overline">Our Approach</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Modulate, don't mask.
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              Most flavor problems in functional nutrition aren't solved by
              adding more flavor. They're solved by changing how the palate
              perceives the base matrix. Our systems work upstream of flavor:
              reducing bitterness at the receptor, managing astringency at the
              mucosal level, and lifting perceived sweetness without
              disproportionate sugar load.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              The result is a cleaner canvas, one where standard flavor
              systems finally deliver the drinkability and repeat use that
              brands need.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="aspect-[5/4] rounded-sm border border-gray-200 overflow-hidden bg-gray-50">
              <img
                src="https://images.pexels.com/photos/8940463/pexels-photo-8940463.jpeg"
                alt="Laboratory glassware"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Principles */}
      <Section testId="about-principles" bg="muted">
        <div className="max-w-2xl">
          <span className="pm-overline">Principles</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
            How we partner with formulators.
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {principles.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="pm-card p-7 md:p-8"
                data-testid={`about-principle-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-sm bg-brand-subtle flex items-center justify-center text-brand mb-5">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Who we serve */}
      <Section testId="about-audience">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="pm-overline">Who We Serve</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Formulators, R&D teams, and the brands they build for.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              "Product developers",
              "R&D teams",
              "Contract manufacturers",
              "Supplement and beverage brands",
            ].map((role, i) => (
              <div
                key={role}
                className="border border-gray-200 rounded-sm px-6 py-5 flex items-center justify-between hover:border-brand transition-colors"
                data-testid={`about-audience-${i}`}
              >
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-1">
                    0{i + 1}
                  </div>
                  <div className="font-heading text-base font-medium text-gray-900">
                    {role}
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-gray-300" />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section testId="about-cta" bg="brand-subtle">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <span className="pm-overline">Work With Us</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Let's talk about your formulation.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Button
              asChild
              data-testid="about-cta-contact"
              className="bg-brand hover:bg-brand-hover text-white rounded-sm h-12 px-7 text-[15px] font-medium"
            >
              <Link to="/contact">
                Start a Conversation
                <ArrowUpRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
